import { FeedPaper } from '../../lib/hooks/useProjectFeed';
import {
    DownvoteReason,
    getFeedbackVoteIcon,
    getFeedbackVoteLabel,
    FeedbackIcon,
    ProjectPaperFeedback,
} from '../../lib/projectPaperFeedback';
import { PaperAuthor } from '../papers/types';
import {
    attachStandalonePaperBackLink,
    attachStandalonePaperProjectContext,
    createSlug,
    getShortId,
} from '../../lib/paperUrl';
import { formatPaperAuthors } from '../../lib/paperAuthors';
import { buildDashboardPath } from '../../lib/dashboardRoutes';
import { getPaperArxivId, getPaperVenueLabel } from '../../lib/sourceHelpers';
import type { MessageDescriptor } from '@lingui/core';
import { msg } from '@lingui/core/macro';
import type { LocalizedMessage } from '../../lib/i18n/messages';

export function formatAuthors(authors: PaperAuthor[] | null | undefined): string {
    return formatPaperAuthors(authors);
}

function getFeedPaperVenue(item: FeedPaper): string | null {
    if (item.paper.venue_name) {
        return item.paper.venue_name;
    }

    return getPaperVenueLabel({
        sourceId: item.paper.source_id,
        sourcePaperId: item.paper.source_paper_id,
        doi: item.paper.doi,
        url: item.paper.url,
    });
}

export function getAlphaXivOverviewUrl(
    sourceId: number | null | undefined,
    sourcePaperId: string | null | undefined,
    url?: string | null | undefined,
    paperResolutionKey?: string | null | undefined,
): string | null {
    const arxivId = getPaperArxivId({
        sourceId,
        sourcePaperId,
        url,
        paperResolutionKey,
    });
    if (!arxivId) {
        return null;
    }

    return `https://www.alphaxiv.org/overview/${encodeURIComponent(arxivId)}`;
}

export function formatRelevanceScore(score: number | null | undefined): MessageDescriptor | null {
    if (score === null || score === undefined) {
        return null;
    }

    return {
        ...msg({ id: 'feed.relevanceScore', message: '{score, number, percent} match' }),
        values: { score },
    };
}

export function getRelevanceClassLabel(relevanceClass: number | null | undefined): MessageDescriptor | null {
    if (relevanceClass === 2) {
        return msg({ id: 'feed.relevance.mustRead', message: 'Must read' });
    }

    if (relevanceClass === 1) {
        return msg({ id: 'feed.relevance.related', message: 'Related' });
    }

    return null;
}

export function formatPublishedDate(
    dateString: string | null | undefined,
    formatLocale = 'en-US',
): string | null {
    if (!dateString) {
        return null;
    }

    try {
        const date = new Date(dateString);
        return date.toLocaleDateString(formatLocale, {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
        });
    } catch {
        return dateString;
    }
}

export function getFeedbackStatusLabel(feedback: ProjectPaperFeedback | null | undefined): MessageDescriptor | null {
    if (!feedback) {
        return null;
    }

    return getFeedbackVoteLabel(feedback.vote);
}

export function getFeedbackStatusIcon(feedback: ProjectPaperFeedback | null | undefined): FeedbackIcon | null {
    if (!feedback) {
        return null;
    }

    return getFeedbackVoteIcon(feedback.vote);
}

export function getDownvoteReasonLabel(reason: DownvoteReason): MessageDescriptor | null {
    if (reason === 'not_relevant') {
        return msg({ id: 'feedback.reason.offTopic', message: 'Off-topic' });
    }

    if (reason === 'low_quality') {
        return msg({ id: 'feedback.reason.lowQuality', message: 'Low quality' });
    }

    return null;
}

export function buildListMetadata(item: FeedPaper): LocalizedMessage[] {
    return [
        getFeedPaperVenue(item),
        formatRelevanceScore(item.relevance_score),
        getRelevanceClassLabel(item.relevance_class),
    ].filter(Boolean) as LocalizedMessage[];
}

export function buildDetailMetadata(item: FeedPaper, formatLocale = 'en-US'): LocalizedMessage[] {
    return [
        getFeedPaperVenue(item),
        formatRelevanceScore(item.relevance_score),
        getRelevanceClassLabel(item.relevance_class),
        formatPublishedDate(item.paper.published_date, formatLocale),
    ].filter(Boolean) as LocalizedMessage[];
}

export function getFeedPaperHref(item: FeedPaper, projectId: string, pathname?: string | null): string {
    const paper = item.paper;
    const paperShortId = paper.short_id || getShortId(paper.id);
    const paperSlug = item.slug
        || (item.paper_title ? createSlug(item.paper_title) : '')
        || (paper.title ? createSlug(paper.title) : 'paper');
    const basePaperUrl = `/p/${paperShortId}/${paperSlug}`;
    const contextualPaperUrl = attachStandalonePaperProjectContext(basePaperUrl, {
        projectId,
        projectPaperId: item.id,
    });

    return attachStandalonePaperBackLink(contextualPaperUrl, {
        backHref: buildDashboardPath(pathname, `/${projectId}`),
        backText: '← Back to Feed',
    });
}
